import { create } from 'zustand';

import { promptText } from '../lib/prompt-dialog.js';
import { parseChord, shortcuts } from '../lib/shortcuts/index.js';
import { useEditorPrefs } from '../state/editor-prefs.js';
import { WORKSPACE_PRESETS, useLayout } from '../state/layout.js';
import { useWorkspace } from '../state/workspace.js';
import { PieSettings } from './PieMenu.js';
import { ShortcutManager } from './ShortcutManager.js';
import { AgentsSettings } from './settings/AgentsSettings.js';
import { IconButton } from './ui/controls.js';
import { Icon, type IconName } from './ui/icons.js';

export type SettingsSection = 'agents' | 'shortcuts' | 'pies' | 'layout' | 'editor';

const SECTIONS: ReadonlyArray<{ id: SettingsSection; label: string; icon: IconName }> = [
  { id: 'agents', label: 'Your agents', icon: 'link' },
  { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: 'command' },
  { id: 'pies', label: 'Pie Menus', icon: 'layers' },
  { id: 'layout', label: 'Layouts & Workspaces', icon: 'layers' },
  { id: 'editor', label: 'Code Editor', icon: 'code' },
];

export const useSettingsView = create<{
  section: SettingsSection;
  /** Shortcut Manager filter requested from elsewhere ("Change Shortcut…", conflict prompt). */
  shortcutFocus: { query?: string; chord?: string; n: number } | null;
}>(() => ({ section: 'shortcuts', shortcutFocus: null }));

/** Show the Settings view at a section. */
export function openSettings(section: SettingsSection): void {
  useSettingsView.setState({ section });
  useWorkspace.getState().setView('settings');
}

/** Open the Shortcut Manager filtered to a command id or a chord. */
export function openShortcutsFor(target: string): void {
  const chord = shortcuts.get(target) ? undefined : (parseChord(target) ?? undefined);
  const n = (useSettingsView.getState().shortcutFocus?.n ?? 0) + 1;
  useSettingsView.setState({ shortcutFocus: chord ? { chord, n } : { query: target, n } });
  openSettings('shortcuts');
}

/** The Settings document: app preferences in sections. */
export function SettingsView() {
  const section = useSettingsView((s) => s.section);
  return (
    <div className="settings-view">
      <nav className="settings-nav" aria-label="Settings sections">
        {SECTIONS.map((s) => (
          <button
            key={s.id}
            type="button"
            className={`settings-nav-item${section === s.id ? ' is-active' : ''}`}
            onClick={() => useSettingsView.setState({ section: s.id })}
          >
            <Icon name={s.icon} size={14} />
            <span>{s.label}</span>
          </button>
        ))}
      </nav>
      <div className="settings-body">
        {section === 'agents' && <AgentsSettings />}
        {section === 'shortcuts' && <ShortcutManager />}
        {section === 'pies' && <PieSettings />}
        {section === 'layout' && <LayoutSettings />}
        {section === 'editor' && <EditorSettings />}
      </div>
    </div>
  );
}

function EditorSettings() {
  const prefs = useEditorPrefs();
  const toggle = (key: 'autoClose' | 'autoIndent' | 'formatOnPaste' | 'suggest' | 'wordWrap' | 'minimap', label: string, hint: string) => (
    <label className="editor-pref">
      <input type="checkbox" checked={prefs[key]} onChange={(e) => prefs.set({ [key]: e.target.checked })} />
      <span>
        <strong>{label}</strong>
        <span className="muted"> {hint}</span>
      </span>
    </label>
  );
  return (
    <div className="layout-settings editor-settings">
      <h3>Code editor</h3>
      <p className="muted">Applies to every code tab right away and is remembered in this browser.</p>
      <label className="editor-pref">
        <span><strong>Font size</strong></span>
        <input type="number" min={8} max={32} step={0.5} value={prefs.fontSize} onChange={(e) => prefs.set({ fontSize: Number(e.target.value) || 13.5 })} />
      </label>
      <label className="editor-pref">
        <span><strong>Tab size</strong></span>
        <input type="number" min={1} max={8} value={prefs.tabSize} onChange={(e) => prefs.set({ tabSize: Math.max(1, Math.min(8, Number(e.target.value) || 2)) })} />
      </label>
      {toggle('autoClose', 'Auto-close brackets and quotes', 'Typing ( [ { " adds the closing one.')}
      {toggle('autoIndent', 'Auto-indent', 'New lines keep and adjust the indentation. Off: typed text is inserted exactly as typed.')}
      {toggle('suggest', 'Suggestions while typing', 'Show the completion list as you type; Enter accepts a suggestion.')}
      {toggle('formatOnPaste', 'Format on paste', 'Reformat pasted code with the language formatter.')}
      {toggle('wordWrap', 'Word wrap', 'Wrap long lines at the editor edge.')}
      {toggle('minimap', 'Minimap', 'Code overview on the right edge.')}
      <div className="layout-saved-actions">
        <button type="button" className="ghost small" onClick={() => prefs.reset()}>
          Reset to defaults
        </button>
      </div>
    </div>
  );
}

function LayoutSettings() {
  const preset = useLayout((s) => s.preset);
  const saved = useLayout((s) => s.saved);
  const apply = (id: string) => {
    const view = useLayout.getState().applyPreset(id);
    if (view) useWorkspace.getState().setView(view as never);
  };
  return (
    <div className="layout-settings">
      <h3>Workspaces</h3>
      <p className="muted">
        Each workspace arranges the views and panels for one kind of work, like Blender&rsquo;s workspace tabs and Unity&rsquo;s
        layouts. Drag any tab onto a panel edge to split it, onto a tab strip to group it, or use a tab&rsquo;s menu to float it
        or open it in its own window.
      </p>
      <div className="layout-presets">
        {WORKSPACE_PRESETS.map((p) => (
          <button key={p.id} type="button" className={`layout-preset${preset === p.id ? ' is-active' : ''}`} onClick={() => apply(p.id)}>
            <strong>{p.label}</strong>
            <span className="muted">{p.description}</span>
          </button>
        ))}
      </div>
      <h3>Saved layouts</h3>
      <div className="layout-saved">
        {Object.keys(saved).length === 0 && <p className="muted">No saved layouts yet.</p>}
        {Object.keys(saved).map((name) => (
          <div key={name} className="layout-saved-row">
            <button type="button" className="ghost small" onClick={() => useLayout.getState().load(name)}>
              {name}
            </button>
            <IconButton icon="trash" title={`Delete "${name}"`} onClick={() => useLayout.getState().remove(name)} size={13} />
          </div>
        ))}
        <div className="layout-saved-actions">
          <button type="button" className="ghost small" onClick={() => void saveLayoutAs()}>
            Save current layout…
          </button>
          <button type="button" className="ghost small" onClick={() => useLayout.getState().reset()}>
            Reset to {WORKSPACE_PRESETS.find((p) => p.id === preset)?.label ?? 'Default'}
          </button>
          <button type="button" className="ghost small" onClick={() => void saveLayoutToFile()}>
            Save to file…
          </button>
          <button type="button" className="ghost small" onClick={loadLayoutFromFile}>
            Load from file…
          </button>
          <button type="button" className="ghost small danger" onClick={revertLayoutsToFactory}>
            Revert factory settings
          </button>
        </div>
      </div>
    </div>
  );
}

export async function saveLayoutAs(): Promise<void> {
  const name = (await promptText('Name this layout'))?.trim();
  if (!name) return;
  // Unity asks before replacing a layout of the same name.
  if (useLayout.getState().saved[name] && !window.confirm(`Overwrite the layout "${name}"?`)) return;
  useLayout.getState().save(name);
}

/** Save the current layout to a `.layout.json` file. */
export async function saveLayoutToFile(): Promise<void> {
  const name = (await promptText('Name the layout file', 'My layout'))?.trim();
  if (!name) return;
  const blob = new Blob([useLayout.getState().exportLayout(name)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${name.replace(/[^\w.-]+/g, '-')}.layout.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

/** Load a `.layout.json` file, apply it and keep it in Saved layouts. */
export function loadLayoutFromFile(): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = '.json,application/json';
  input.onchange = async () => {
    const file = input.files?.[0];
    if (!file) return;
    if (!useLayout.getState().importLayout(await file.text())) window.alert(`"${file.name}" is not a layout file.`);
  };
  input.click();
}

/** Unity's Revert Factory Settings: drop saved layouts, back to Default. */
export function revertLayoutsToFactory(): void {
  if (window.confirm('Delete all saved layouts and restore the Default workspace?')) useLayout.getState().revertFactory();
}
